import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';
import { CompanyMemberSearchItemType } from 'types/api/companyMemberSearchItemType';
import SettingModalMemberInvitation from './component/modal/SettingModalMemberInvitation';

/**
 *　カンパニーメンバー設定画面
 */
export default function SettingMember(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const [acctState] = useRecoilState(AcctState);

  const { noticeMessageSuccess, confirm } = useAppState();
  const { postCompanyMemberSearch, postInvitationResend, postInvitationDelete } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [membersParticipatedState, setMembersParticipatedState] = useState<
    CompanyMemberSearchItemType[]
  >([]);
  const [membersInvitedState, setMembersInvitedState] = useState<CompanyMemberSearchItemType[]>([]);
  const [invitationModalOpenState, setInvitationModalOpenState] = useState(false);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        const response = await postCompanyMemberSearch({
          companyId: acctState.company.id,
        });

        // 参加中メンバー一覧
        setMembersParticipatedState(
          response.accounts.filter(item => {
            return (
              item.member.companyMemberStatusDiv ==
              DivConst.items.companyMemberStatus.participated.value
            );
          })
        );

        // 招待中メンバー一覧
        setMembersInvitedState(
          response.accounts.filter(item => {
            return (
              item.member.companyMemberStatusDiv == DivConst.items.companyMemberStatus.invited.value
            );
          })
        );
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　ユーザー追加ボタン押下時の処理
   */
  const onInvite = () => {
    setInvitationModalOpenState(true);
  };

  /**
   *　編集ボタン押下時の処理
   */
  const onEdit = (item: CompanyMemberSearchItemType) => {
    navigate(`/setting/${acctState.company.cd}/member/edit`, {
      state: {
        companyMemberId: item.member.id,
        userName: item.user.name,
        userMailAddress: item.user.mailAddress,
        roleDiv: item.member?.roleDiv,
      },
    });
  };

  /**
   *　編集再送信ボタン押下時の処理
   */
  const onInvitationResend = (item: CompanyMemberSearchItemType) => {
    confirm({
      title: '招待の再送信',
      message: `「${item.member.invitationMailAddress}」に招待メールを再送信よろしいですか？`,
      onSubmit: async () => {
        try {
          // ボタンローディングの表示
          setProcessing(true);

          // クイズ複製APIの呼び出し
          await postInvitationResend({
            companyMemberId: item.member.id,
          });
          // 完了メッセージの表示
          noticeMessageSuccess('招待メールの再送信が完了しました。');
        } catch (e) {
          showBoundary(e);
        } finally {
          setProcessing(false);
        }
      },
    });
  };

  /**
   *　招待取消ボタン押下時の処理
   */
  const onInvitationDelete = (member: CompanyMemberSearchItemType) => {
    confirm({
      title: '招待の取り消し',
      message: `「${member.member.invitationMailAddress}」を招待を取り消してもよろしいですか？`,
      onSubmit: async () => {
        try {
          // ボタンローディングの表示
          setProcessing(true);

          // クイズ複製APIの呼び出し
          await postInvitationDelete({
            companyMemberId: member.member.id,
          });
          // 完了メッセージの表示
          noticeMessageSuccess('招待の取り消しが完了しました。');
          // ステートの更新
          setMembersInvitedState(
            membersInvitedState.filter(item => item.member.id != member.member.id)
          );
        } catch (e) {
          showBoundary(e);
        } finally {
          setProcessing(false);
        }
      },
    });
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 管理権限を持っているかかどうかを判定
   */
  const _isAdmin = (): boolean => {
    return acctState.company.member?.roleDiv === DivConst.items.role.admin.value;
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-header">
        <div className="container">
          <div className="d-md-flex d-flex-header">
            <div className="flex-shrink-0 flex-left">
              <h2 className="flex-title">ユーザーの一覧</h2>
            </div>
            {_isAdmin() && (
              <div className="flex-grow-1 flex-right">
                <ul className="list-inline list-btn mb-0">
                  <li className="list-inline-item">
                    <a onClick={onInvite} className="btn btn-common btn-common-w147 moderate-blue">
                      ユーザー追加
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 初期化中の表示 */}
      <IndicatorScreen visible={!initialized || processing} />
      {/* 初期化後の表示 */}
      {initialized && (
        <>
          {/* 参加済のユーザー一覧 */}
          <section className="section-users">
            <div className="container">
              <ul className="list-unstyled list-users d-flex flex-wrap justify-content-start align-items-start">
                {membersParticipatedState.map(item => {
                  return (
                    <li key={item.member.id}>
                      <div className="card">
                        <div className="card-body">
                          <h5 className="card-name">{item.user.name}</h5>
                          <h6 className="card-position mb-2 text-muted">
                            {AppHelper.getDivLabel('role', item.member?.roleDiv)}
                          </h6>
                          <p className="card-email">{item.user.mailAddress}</p>
                          {_isAdmin() && (
                            <a
                              onClick={() => onEdit(item)}
                              className="btn btn-common d-inline-block moderate-blue"
                            >
                              編集
                            </a>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
          {/* 招待中のユーザー一覧 */}
          {membersInvitedState.length > 0 && (
            <>
              <section className="section-header">
                <div className="container">
                  <div className="d-md-flex d-flex-header">
                    <div className="flex-shrink-0 flex-left">
                      <h2 className="flex-title">招待中ユーザーの一覧</h2>
                    </div>
                  </div>
                </div>
              </section>
              <section className="section-users">
                <div className="container">
                  <ul className="list-unstyled list-users d-flex flex-wrap justify-content-start align-items-start">
                    {membersInvitedState.map(item => {
                      return (
                        <li key={item.member.id}>
                          <div className="card">
                            <div className="card-body">
                              <h5 className="card-name">No Name</h5>
                              <h6 className="card-position mb-2 text-muted">
                                {AppHelper.getDivLabel('role', item.member?.roleDiv)}
                              </h6>
                              <p className="card-email">{item.member.invitationMailAddress}</p>
                              {_isAdmin() && (
                                <>
                                  <a
                                    onClick={() => onInvitationResend(item)}
                                    className="btn btn-common d-inline-block moderate-blue me-2 mb-2"
                                  >
                                    再送信
                                  </a>
                                  <a
                                    onClick={() => onInvitationDelete(item)}
                                    className="btn btn-common d-inline-block outline-blue mb-2"
                                  >
                                    招待取消
                                  </a>
                                </>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </section>
            </>
          )}
          {/* カンパニーメンバー招待モーダル */}
          <SettingModalMemberInvitation
            open={invitationModalOpenState}
            setOpen={setInvitationModalOpenState}
            membersInvitedState={membersInvitedState}
            setMembersInvitedState={setMembersInvitedState}
          />
        </>
      )}
    </>
  );
}
